// /client/src/components/EditPod.js
import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navigation from './Navigation';

const EditPod = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  
  // Pod state
  const [pod, setPod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Active deployment state
  const [inActiveDeployment, setInActiveDeployment] = useState(null);
  
  // Available contacts state
  const [allContacts, setAllContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  
  // Selection state for adding new members
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  
  // Edit pod modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editPodName, setEditPodName] = useState('');
  const [editPodId, setEditPodId] = useState('');
  const [updatingPod, setUpdatingPod] = useState(false);
  const [editPodStatus, setEditPodStatus] = useState('');
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({
    key: 'fullName',
    direction: 'ascending'
  });

  // Check if editing is disabled (pod is in active deployment)
  const isEditingDisabled = useMemo(() => {
    return inActiveDeployment !== null;
  }, [inActiveDeployment]);

  // Fetch pod details
  const fetchPod = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      const response = await fetch(`/api/pods/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPod(data.pod);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch pod');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setError('Failed to load pod');
    } finally {
      setLoading(false);
    }
  };

  // Check if pod is in an active deployment
  const checkDeploymentStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`/api/pods/${id}/deployment-status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setInActiveDeployment(data.inActiveDeployment || null);
      }
    } catch (error) {
      console.error('Error checking deployment status:', error);
    }
  };

  // Fetch all contacts for adding new members
  const fetchContacts = async () => {
    try {
      setLoadingContacts(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch('/api/contacts', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAllContacts(data.contacts || []);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoadingContacts(false);
    }
  };

  useEffect(() => {
    fetchPod();
    checkDeploymentStatus();
  }, [id]);

  // Open edit modal
  const handleOpenEditModal = () => {
    if (isEditingDisabled) return;
    setEditPodName(pod.podName);
    setEditPodId(pod.podId);
    setEditPodStatus(pod.status);
    setShowEditModal(true);
  };

  // Update pod
  const handleUpdatePod = async () => {
    if (isEditingDisabled) return;
    if (!editPodName.trim() || !editPodId.trim()) {
      setError('Please provide both Pod ID and Pod Name');
      return;
    }

    try {
      setUpdatingPod(true);
      setError('');
      const token = localStorage.getItem('token');
      
      const response = await fetch(`/api/pods/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          podId: editPodId.trim(),
          podName: editPodName.trim(),
          status: editPodStatus
        })
      });

      if (response.ok) {
        setSuccessMessage('Pod updated successfully');
        setShowEditModal(false);
        fetchPod();
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update pod');
      }
    } catch (error) {
      console.error('Error updating pod:', error);
      setError('Failed to update pod');
    } finally {
      setUpdatingPod(false);
    }
  };

  // Remove member from pod
  const handleRemoveMember = async (userId) => {
    if (isEditingDisabled) return;
    if (!window.confirm('Are you sure you want to remove this member from the pod?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`/api/pods/${id}/members/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setSuccessMessage('Member removed successfully');
        fetchPod();
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to remove member');
      }
    } catch (error) {
      console.error('Error removing member:', error);
      setError('Failed to remove member');
    }
  };

  // Open add members modal
  const handleOpenAddMembers = () => {
    if (isEditingDisabled) return;
    fetchContacts();
    setShowAddMembersModal(true);
    setSelectedContacts([]);
  };

  // Add selected members to pod
  const handleAddMembers = async () => {
    if (isEditingDisabled) return;
    if (selectedContacts.length === 0) {
      setError('Please select at least one contact to add');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      const promises = selectedContacts.map(userId =>
        fetch(`/api/pods/${id}/members`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ userId, role: 'participant' })
        })
      );

      const results = await Promise.all(promises);
      const allSucceeded = results.every(r => r.ok);
      
      if (allSucceeded) {
        setSuccessMessage(`${selectedContacts.length} member${selectedContacts.length !== 1 ? 's' : ''} added successfully`);
        setShowAddMembersModal(false);
        setSelectedContacts([]);
        fetchPod();
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError('Some members could not be added');
      }
    } catch (error) {
      console.error('Error adding members:', error);
      setError('Failed to add members');
    }
  };

  // Filter contacts to exclude current members
  const availableContacts = useMemo(() => {
    if (!pod || !allContacts.length) return [];
    
    const currentMemberIds = pod.members
      .filter(m => m.status === 'active' && m.userId)
      .map(m => m.userId._id || m.userId.id);
    
    return allContacts.filter(contact => 
      !currentMemberIds.includes(contact._id || contact.id)
    );
  }, [pod, allContacts]);

  // Filter and sort available contacts
  const filteredAndSortedContacts = useMemo(() => {
    let filtered = [...availableContacts];
    
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(contact => {
        const fullName = contact.fullName?.toLowerCase() || '';
        const email = contact.email?.toLowerCase() || '';
        const phone = contact.phone?.toLowerCase() || '';
        
        return fullName.includes(searchLower) || 
               email.includes(searchLower) || 
               phone.includes(searchLower);
      });
    }
    
    if (selectedRole) {
      filtered = filtered.filter(contact => contact.role === selectedRole);
    }
    
    if (selectedStatus) {
      filtered = filtered.filter(contact => contact.status === selectedStatus);
    }
    
    if (sortConfig !== null) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        if (sortConfig.key === 'createdAt') {
          aValue = new Date(aValue || 0);
          bValue = new Date(bValue || 0);
        } else {
          aValue = (aValue || '').toString().toLowerCase();
          bValue = (bValue || '').toString().toLowerCase();
        }
        
        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return filtered;
  }, [availableContacts, searchTerm, selectedRole, selectedStatus, sortConfig]);

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnName) => {
    const iconStyle = {
      width: '12px',
      height: '12px',
      marginLeft: '8px',
      display: 'inline-block'
    };

    if (sortConfig.key !== columnName) {
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 5.83L15.17 9H13v6h-2V9H8.83L12 5.83zM12 18.17L8.83 15H11V9h2v6h2.17L12 18.17z"/>
        </svg>
      );
    }
    
    if (sortConfig.direction === 'ascending') {
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 6.83L15.17 10H13v8h-2v-8H8.83L12 6.83z"/>
        </svg>
      );
    } else {
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 17.17L8.83 14H11V6h2v8h2.17L12 17.17z"/>
        </svg>
      );
    }
  };

  const isEligibleForPod = (contact) => {
    return contact.role === 'participant' && 
           contact.smsVerified === true && 
           !contact.activePod; // Must not be in an active pod
  };

  const getIneligibleReason = (contact) => {
    if (contact.role !== 'participant') {
      return 'Only participants can be added to pods';
    }
    if (!contact.smsVerified) {
      return 'Contact has not opted in to SMS';
    }
    if (contact.activePod) {
      return `Already in pod: ${contact.activePod.podName}`;
    }
    return '';
  };

  const eligibleContacts = useMemo(() => {
    return filteredAndSortedContacts.filter(isEligibleForPod);
  }, [filteredAndSortedContacts]);

  // Get contacts that are already in an active pod from filtered list
  const contactsAlreadyInPod = useMemo(() => {
    return filteredAndSortedContacts.filter(contact => contact.activePod);
  }, [filteredAndSortedContacts]);

  const handleSelectContact = (id) => {
    const contact = filteredAndSortedContacts.find(c => (c.id || c._id) === id);
    if (!contact || !isEligibleForPod(contact)) {
      return;
    }
    
    setSelectedContacts(prev => {
      if (prev.includes(id)) {
        return prev.filter(contactId => contactId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleSelectAll = () => {
    const eligibleIds = eligibleContacts.map(c => c.id || c._id);
    
    if (selectedContacts.length === eligibleIds.length && eligibleIds.length > 0) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(eligibleIds);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      timeZone: 'America/Los_Angeles',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return 'is-danger';
      case 'host':
        return 'is-warning';
      case 'participant':
      default:
        return 'is-primary';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'is-success';
      case 'inactive':
        return 'is-light';
      default:
        return 'is-light';
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedRole('');
    setSelectedStatus('');
  };

  if (loading) {
    return (
      <section className="theme-light m-0 p-0" data-theme="light">
        <div className='is-flex is-flex-direction-column is-justify-content-center'>
          <Navigation />
          <div className='mt-6 pt-6 container'>
            <div className="has-text-centered py-6">
              <div className="is-loading"></div>
              <p>Loading pod...</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!pod) {
    return (
      <section className="theme-light m-0 p-0" data-theme="light">
        <div className='is-flex is-flex-direction-column is-justify-content-center'>
          <Navigation />
          <div className='mt-6 pt-6 container'>
            <div className="notification is-danger">
              Pod not found
            </div>
          </div>
        </div>
      </section>
    );
  }

  const activeMembers = pod.members.filter(m => m.status === 'active' && m.userId);

  return (
    <>
      <section className="theme-light m-0 p-0" data-theme="light">
        <div className='is-flex is-flex-direction-column is-justify-content-center'>
          
          <Navigation />

          <div className='mt-2 pt-6 container px-6'>
            {/* Header */}
            <div className='pb-4 is-flex is-justify-content-space-between is-align-items-center'>
              <div>
                <button 
                  className="button is-small is-text mb-4"
                  onClick={() => navigate(-1)}
                >
                  ← Back
                </button>
                <div className="is-flex is-align-items-center">
                  <h1 className='h1 my-2 mr-2'>{pod.podName}</h1>
                  {/* Only show edit button if not in active deployment */}
                  {!isEditingDisabled && (
                    <button
                      className="button is-small is-ghost"
                      onClick={handleOpenEditModal}
                      title="Edit pod details"
                    >
                      <span className="icon is-small">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      </span>
                    </button>
                  )}
                </div>
                <p className="has-text-grey">
                  <code className="is-size-7">{pod.podId}</code>
                </p>
              </div>
              <div className="buttons">
                {/* Only show Add Members button if not in active deployment */}
                {!isEditingDisabled && (
                  <button 
                    className="button is-primary"
                    onClick={handleOpenAddMembers}
                  >
                    Add Members
                  </button>
                )}
              </div>
            </div>

            {/* Warning banner when pod is in active deployment */}
            {inActiveDeployment && (
              <div className="notification is-warning is-light mb-4">
                <div className="is-flex is-align-items-center">
                  <span className="icon mr-2">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                      <line x1="12" y1="9" x2="12" y2="13"/>
                      <line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                  </span>
                  <div>
                    <strong>Pod is in an active deployment</strong>
                    <p className="is-size-7 mb-0">
                      This pod is part of deployment "<strong>{inActiveDeployment.deploymentName}</strong>" 
                      (Status: {inActiveDeployment.deploymentStatus}). 
                      Editing members is disabled while the deployment is active.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Success Message */}
            {successMessage && (
              <div className="notification is-success">
                <button 
                  className="delete" 
                  onClick={() => setSuccessMessage('')}
                ></button>
                {successMessage}
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="notification is-danger">
                <button 
                  className="delete" 
                  onClick={() => setError('')}
                ></button>
                {error}
              </div>
            )}

            {/* Current Members */}
            <div className="box">
              <h2 className="title is-5">Current Members ({activeMembers.length})</h2>
              
              {activeMembers.length === 0 ? (
                <div className="notification is-info is-light">
                  <p>No active members in this pod. Click "Add Members" to get started.</p>
                </div>
              ) : (
                <div className="table-container">
                  <table className="table is-fullwidth is-hoverable">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Role</th>
                        <th>Joined</th>
                        {/* Only show Actions column if editing is allowed */}
                        {!isEditingDisabled && (
                          <th style={{ width: '100px' }}>Actions</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {activeMembers.map((member) => {
                        const contact = member.userId;
                        
                        return (
                          <tr key={member._id}>
                            <td>{contact.profile?.firstName} {contact.profile?.lastName}</td>
                            <td>{contact.email}</td>
                            <td>{contact.phone || contact.profile?.phone || 'Not provided'}</td>
                            <td>
                                <span className={`tag ${getRoleColor(contact.role)}`}>
                                    {contact.role.charAt(0).toUpperCase() + contact.role.slice(1)}
                                </span>
                            </td>
                            <td>{formatDate(member.joinedAt)}</td>
                            {/* Only show Remove button if editing is allowed */}
                            {!isEditingDisabled && (
                              <td>
                                <button
                                  className="button is-small is-danger is-light"
                                  onClick={() => handleRemoveMember(contact._id || contact.id)}
                                >
                                  Remove
                                </button>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          </div>

        </div>
      </section>

      {/* Edit Pod Modal */}
      {showEditModal && (
        <div className="modal is-active">
          <div className="modal-background" onClick={() => setShowEditModal(false)}></div>
          <div className="modal-card">
            <header className="modal-card-head">
              <h1 className="modal-card-title colorBlack is-bold">Edit Pod Details</h1>
              <button 
                className="delete" 
                aria-label="close"
                onClick={() => setShowEditModal(false)}
              ></button>
            </header>
            <section className="my-0 py-0 modal-card-body">
              <div className="my-0 py-0 field">
                <label className="my-0 py-0 label">Pod ID</label>
                <div className="control">
                  <input
                    className="input"
                    type="text"
                    placeholder="POD-12345"
                    value={editPodId}
                    onChange={(e) => setEditPodId(e.target.value)}
                  />
                </div>
                <p className="mx-2 pt-1 is-size-7">Unique identifier for this pod</p>
              </div>

              <div className="field">
                <label className="label">Pod Name</label>
                <div className="control">
                  <input
                    className="input"
                    type="text"
                    placeholder="Enter pod name..."
                    value={editPodName}
                    onChange={(e) => setEditPodName(e.target.value)}
                    autoFocus
                  />
                </div>
              </div>

              <div className="field">
                <label className="label">Status</label>
                <div className="control">
                  <div className="select is-fullwidth">
                    <select
                      value={editPodStatus}
                      onChange={(e) => setEditPodStatus(e.target.value)}
                    >
                      <option value="active">Active</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                </div>
                <p className="mx-2 pt-1 is-size-7">Current state of this pod</p>
              </div>
            </section>
            <footer className="modal-card-foot">
              <button 
                className={`mr-4 button is-success ${updatingPod ? 'is-loading' : ''}`}
                onClick={handleUpdatePod}
                disabled={!editPodName.trim() || !editPodId.trim() || updatingPod}
              >
                Update Pod
              </button>
              <button 
                className="button" 
                onClick={() => setShowEditModal(false)}
                disabled={updatingPod}
              >
                Cancel
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* Add Members Modal */}
      {showAddMembersModal && (
        <div className="modal is-active">
          <div className="modal-background" onClick={() => setShowAddMembersModal(false)}></div>
          <div className="modal-card" style={{ width: '90%', maxWidth: '1200px' }}>
            <header className="modal-card-head">
              <h1 className="modal-card-title is-none is-bold colorBlack">Add Members to {pod.podName}</h1>
              <button 
                className="delete" 
                aria-label="close"
                onClick={() => setShowAddMembersModal(false)}
              ></button>
            </header>
            <section className="modal-card-body">
              {/* Eligibility Notice */}
              <div className="notification is-info is-light mb-4">
                <p className="is-size-7">
                  <strong>Note:</strong> Only participants who have opted in to SMS (verified) and are not already in another pod can be added. 
                  Ineligible contacts are greyed out.
                </p>
              </div>

              {/* Search and Filter */}
              <div className="box mb-4">
                <div className="columns">
                  <div className="column is-5">
                    <div className="field">
                      <label className="pt-0 label">Search</label>
                      <div className="control has-icons-left">
                        <input
                          className="input"
                          type="text"
                          placeholder="Search by name, email, or phone..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <span className="icon is-small is-left">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <circle cx="11" cy="11" r="8"/>
                            <path d="m21 21-4.35-4.35"/>
                          </svg>
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="column is-3">
                    <div className="field">
                      <label className="pt-0 label">Filter by Role</label>
                      <div className="control">
                        <div className="select is-fullwidth">
                          <select
                            value={selectedRole}
                            onChange={(e) => setSelectedRole(e.target.value)}
                          >
                            <option value="">All Roles</option>
                            <option value="participant">Participant</option>
                            <option value="host">Host</option>
                            <option value="admin">Admin</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="column is-3">
                    <div className="field">
                      <label className="label">Filter by Status</label>
                      <div className="control">
                        <div className="select is-fullwidth">
                          <select
                            value={selectedStatus}
                            onChange={(e) => setSelectedStatus(e.target.value)}
                          >
                            <option value="">All Statuses</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="column is-1">
                    <div className="field">
                      <label className="label">&nbsp;</label>
                      <div className="control">
                        <button 
                          className="button is-light is-fullwidth"
                          onClick={clearFilters}
                          disabled={!searchTerm && !selectedRole && !selectedStatus}
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Warning: Contacts already in active pods */}
              {contactsAlreadyInPod.length > 0 && (
                <div className="notification is-warning is-light mb-4">
                  <p className="is-size-7">
                    <strong>⚠️ {contactsAlreadyInPod.length}</strong> contact{contactsAlreadyInPod.length !== 1 ? 's are' : ' is'} already in another active pod and cannot be added.
                  </p>
                </div>
              )}

              {loadingContacts ? (
                <div className="has-text-centered py-6">
                  <p>Loading contacts...</p>
                </div>
              ) : filteredAndSortedContacts.length === 0 ? (
                <div className="notification is-info is-light">
                  <p>
                    {availableContacts.length === 0
                      ? 'All contacts are already members of this pod.'
                      : 'No contacts match your search criteria.'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="mb-3">
                    <p className="is-size-7">
                      {selectedContacts.length > 0 && (
                        <span>{selectedContacts.length} selected • </span>
                      )}
                      Showing {filteredAndSortedContacts.length} of {availableContacts.length} available contact{availableContacts.length !== 1 ? 's' : ''}
                      {' • '}
                      <span className="has-text-success has-text-weight-semibold">
                        {eligibleContacts.length} eligible
                      </span>
                      {contactsAlreadyInPod.length > 0 && (
                        <span className="has-text-warning has-text-weight-semibold ml-2">
                          • {contactsAlreadyInPod.length} in other pods
                        </span>
                      )}
                    </p>
                  </div>

                  <div className="table-container" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <table className="table is-fullwidth is-hoverable">
                      <thead>
                        <tr>
                          <th style={{ width: '40px' }}>
                            <input
                              type="checkbox"
                              checked={selectedContacts.length === eligibleContacts.length && eligibleContacts.length > 0}
                              onChange={handleSelectAll}
                              disabled={eligibleContacts.length === 0}
                              title={eligibleContacts.length === 0 ? 'No eligible contacts to select' : 'Select all eligible contacts'}
                            />
                          </th>
                          <th 
                            onClick={() => handleSort('fullName')}
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            className={sortConfig.key === 'fullName' ? 'colorBlack' : 'colorBlue'}
                          >
                            Name {getSortIcon('fullName')}
                          </th>
                          <th 
                            onClick={() => handleSort('email')}
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            className={sortConfig.key === 'email' ? 'colorBlack' : 'colorBlue'}
                          >
                            Email {getSortIcon('email')}
                          </th>
                          <th 
                            onClick={() => handleSort('phone')}
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            className={sortConfig.key === 'phone' ? 'colorBlack' : 'colorBlue'}
                          >
                            Phone {getSortIcon('phone')}
                          </th>
                          <th>
                            Role
                          </th>
                          <th>
                            SMS Opted In
                          </th>
                          <th
                            onClick={() => handleSort('status')}
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            className={sortConfig.key === 'status' ? 'colorBlack' : 'colorBlue'}
                          >
                            Status {getSortIcon('status')}
                        </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAndSortedContacts.map((contact) => {
                          const id = contact.id || contact._id;
                          const isSelected = selectedContacts.includes(id);
                          const eligible = isEligibleForPod(contact);
                          const ineligibleReason = getIneligibleReason(contact);
                          const isInAnotherPod = contact.activePod;
                          
                          return (
                            <tr 
                              key={id}
                              style={{
                                backgroundColor: isSelected ? '#f0f8ff' : 'transparent',
                                opacity: eligible ? 1 : 0.5,
                                cursor: eligible ? 'pointer' : 'not-allowed'
                              }}
                              title={eligible ? '' : ineligibleReason}
                              onClick={() => eligible && handleSelectContact(id)}
                            >
                              <td onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleSelectContact(id)}
                                  disabled={!eligible}
                                  title={eligible ? '' : ineligibleReason}
                                  style={{ cursor: eligible ? 'pointer' : 'not-allowed' }}
                                />
                              </td>
                              <td>
                                {contact.fullName}
                                {isInAnotherPod && (
                                  <span className="ml-2 tag is-danger is-light is-small" title={`In pod: ${contact.activePod.podName}`}>
                                    In Pod: {contact.activePod.podName}
                                  </span>
                                )}
                                {!eligible && contact.role === 'participant' && !isInAnotherPod && (
                                  <span className="ml-2 tag is-warning is-light is-small">
                                    Not opted in
                                  </span>
                                )}
                                {contact.role !== 'participant' && (
                                  <span className="ml-2 tag is-light is-small">
                                    Not a participant
                                  </span>
                                )}
                              </td>
                              <td>{contact.email}</td>
                              <td>{contact.phone || contact.profile?.phone || 'Not provided'}</td>
                              <td>
                                <span className={`tag ${getRoleColor(contact.role)}`}>
                                  {contact.role.charAt(0).toUpperCase() + contact.role.slice(1)}
                                </span>
                              </td>
                              <td>
                                {contact.smsVerified ? (
                                  <span className="tag is-success is-light">Yes</span>
                                ) : (
                                  <span className="tag is-danger is-light">No</span>
                                )}
                              </td>
                              <td>
                                <span className={`tag ${getStatusColor(contact.status)}`}>
                                  {contact.status.charAt(0).toUpperCase() + contact.status.slice(1)}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </section>
            <footer className="modal-card-foot">
              <button 
                className="mr-4 button is-success"
                onClick={handleAddMembers}
                disabled={selectedContacts.length === 0}
              >
                Add {selectedContacts.length} Member{selectedContacts.length !== 1 ? 's' : ''}
              </button>
              <button 
                className="button" 
                onClick={() => setShowAddMembersModal(false)}
              >
                Cancel
              </button>
            </footer>
          </div>
        </div>
      )}
    </>
  );
};

export default EditPod;